import React, { useState, useContext, useMemo, useCallback } from 'react';
import { 
  DialogTitle, DialogContent, DialogActions, Button,
  FormControl, InputLabel, MenuItem, Tabs, Tab, Box, Typography, 
  TextField, Collapse, Alert, AlertTitle, List, ListItem, ListItemText, CircularProgress,
  RadioGroup, FormControlLabel, Radio, FormLabel
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import WidgetsIcon from '@mui/icons-material/Widgets';
import BoltIcon from '@mui/icons-material/Bolt';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { useQuery } from '@tanstack/react-query';

import AuthContext from './auth/Context';
import { WIDGET_COLORS, SHORTCUT_ICONS } from './DynamicWidgetConfigs';
import { parseDataListConfig } from "./DataDynamicPage";
import ManagedDropdown from "./ManagedDropdown";
import Dialog from "./ManagedModal";
import {getValidItems} from './SidebarMenu'
import { ChonCongTyWidget } from './DynamicWidgetCongTyDialog';
import { hoTroNhieuCongTy } from './dashboardCongTy';

const WidgetAddDialog = ({ open, onClose, onAdd, systemWidgets }) => {
  const context = useContext(AuthContext);
  
  const userToken = useMemo(() => context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : ''), [context]);
  const appInfoId = context.appInfo?._id; 

  const [activeTab, setActiveTab] = useState('pivot');
  const [selectedWidgetDef, setSelectedWidgetDef] = useState('');
  const [selectedSize, setSelectedSize] = useState(6);
  const [showPivotGuide, setShowPivotGuide] = useState(false);
  
  const [customTitle, setCustomTitle] = useState('');
  const [listAction, setListAction] = useState('list'); 
  // Công ty lấy dữ liệu (widgetConfig.id_apps); rỗng = công ty đang đăng nhập như cũ.
  const [dsCongTy, setDsCongTy] = useState([]);

  const [systemWidgetColor, setSystemWidgetColor] = useState('');
  const [systemWidgetIcon, setSystemWidgetIcon] = useState('');
  
  const [shortcutData, setShortcutData] = useState({ 
    title: '', url: '', icon: 'link', color: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)' 
  });

  // ============================================================================
  // LẤY DANH SÁCH CHỨC NĂNG TỪ CONTEXT (CHO SHORTCUT)
  // ============================================================================
  const flattenedModules = useMemo(() => {
    // Ưu tiên lấy context.modules hoặc context.appInfo.modules
    const mods = context.modules || context.appInfo?.modules || [];
    const flat = [];
    mods.forEach(mod => {
      if (mod.items && Array.isArray(mod.items)) {
        getValidItems(mod.items).filter(i=>i.code!=="dashboard" && i.code!=="home").forEach(item => {
          if (item.path) {
            flat.push({
              group: mod.title || 'Chức năng khác',
              title: item.title,
              path: item.path,
              code: item.code
            });
          }
        });
      }
    });
    return flat;
  }, [context.modules, context.appInfo]);

  // ============================================================================
  // FETCH DATA
  // ============================================================================
  const { data: listInfoData = [], isLoading: isLoadingListInfo } = useQuery({
    queryKey: ['listinfo', "dynmaicdashboard"],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'listinfo', { limit: 10000, fields: "code,title,api_code", condition: {status: true, is_dashboard_item: true } });
      return res?.data || res || [];
    },
    enabled: open && !!userToken,
  });

  const { data: dataListConfig = [], isLoading: isLoadDataListConfig } = useQuery({
    queryKey: ['data_list_config', "dynmaicdashboard"],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'data_list_config', { limit: 10000});
      return res?.data || res || [];
    },
    enabled: open && !!userToken,
  });

  const { data: availablePivots = [], isLoading: isLoadingPivots } = useQuery({
    queryKey: ['pivotconfigs', appInfoId],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'pivotconfig', { limit: 100 });
      const list = res?.data || res || [];
      // Bỏ qua các bản ghi 'view_*' — đó là góc nhìn tuỳ chỉnh của báo cáo danh sách
      // (ReportDataViewer), không phải cấu hình Pivot nên không dựng được widget pivot.
      return list.filter(c => !String(c.code || '').startsWith('view_'));
    },
    enabled: open && !!appInfoId && !!userToken,
  });

  const { data: availableReports = [], isLoading: isLoadingReports } = useQuery({
    queryKey: ['reportconfig', appInfoId],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'reportconfig', { limit: 1000, fields: 'code,title' });
      return res?.data || res || [];
    },
    enabled: open && !!userToken,
  });

  const { data: availableWorkflows = [], isLoading: isLoadingWorkflows } = useQuery({
    queryKey: ['workflows', appInfoId],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'workflow', { limit: 100 });
      return res?.data || res || [];
    },
    enabled: open && !!userToken, 
  });

  const { data: availableFlows = [], isLoading: isLoadingFlows } = useQuery({
    queryKey: ['flows', appInfoId],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'flow', { limit: 100 });
      return res?.data || res || [];
    },
    enabled: open && !!userToken, 
  });

  // Trang tuỳ biến do usermodule/gói mở rộng cài đặt. `custompage` là model DÙNG CHUNG (không
  // theo id_app) nên chỉ cần lọc status.
  const { data: availableCustomPages = [], isLoading: isLoadingCustomPages } = useQuery({
    queryKey: ['custompage', 'dynamicdashboard'],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'custompage', { limit: 500, fields: 'code,title,icon', condition: { status: true } });
      return res?.data || res || [];
    },
    enabled: open && !!userToken,
  });

  // ============================================================================
  // MEMOIZED DATA PROCESSING
  // ============================================================================
  const combinedSystemWidgets = useMemo(() => {
    const staticWidgets = systemWidgets || [];
    const gradientColors = WIDGET_COLORS.filter(c => c.isGradient).map(c => c.value);

    const dynamicWidgets = listInfoData.map((item, index) => ({
      id: `sys_counter_${item.code}`, 
      type: 'counter', 
      title: `Tổng số ${item.title.toLowerCase().replace("danh mục","").replace("danh sách","")}`, 
      defaultW: 3, 
      defaultH: 3, 
      widgetConfig: { 
        apiCode: item.code, 
        condition: {}, 
        color: gradientColors[index % gradientColors.length] || '#007aff', 
        url: `/page/list/${item.code}` 
      }
    }));

    const dataLists = dataListConfig.map(config => ({
      id: `datalist_${config.moduleCode}`, 
      type: 'list', 
      title: config.title, 
      defaultW: 6, 
      defaultH: 10, 
      widgetConfig: parseDataListConfig(config)
    }));

    return [...staticWidgets, ...dataLists, ...dynamicWidgets];
  }, [systemWidgets, listInfoData, dataListConfig]);

  const colorOptionsNodes = useMemo(() => {
    return WIDGET_COLORS.map(c => (
      <MenuItem key={c.value} value={c.value}>
        {c.isGradient ? (
          <span style={{ background: c.value, color: c.textColor || 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', border: c.value === '#ffffff' ? '1px solid #ddd' : 'none' }}>
            {c.label}
          </span>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: c.value, border: c.value === '#ffffff' ? '1px solid #ddd' : 'none' }}></span>
            {c.label}
          </span>
        )}
      </MenuItem>
    ));
  }, []);

  const iconOptionsNodes = useMemo(() => {
    return SHORTCUT_ICONS.map(item => {
      const IconComp = item.icon;
      return (
        <MenuItem key={item.value} value={item.value}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconComp fontSize="small" sx={{ color: 'text.secondary' }} />
            <span>{item.label}</span>
          </Box>
        </MenuItem>
      );
    });
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleTabChange = useCallback((e, v) => {
    setActiveTab(v);
    setSelectedWidgetDef("");
    setSystemWidgetColor('');
    setSystemWidgetIcon(''); 
    setShowPivotGuide(false);
    setCustomTitle(''); 
    setListAction('list'); 
    setDsCongTy([]);
    // Reset form phím tắt khi chuyển tab
    setShortcutData({ title: '', url: '', icon: 'link', color: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)' });
  }, []);

  const handleConfirmAdd = useCallback(() => {
    let widgetData = null;

    if (activeTab === 'shortcut') {
      if (!shortcutData.title || !shortcutData.url) return;
      widgetData = { 
        type: 'shortcut', 
        title: shortcutData.title, 
        widgetConfig: { url: shortcutData.url, icon: shortcutData.icon, color: shortcutData.color } 
      };
    } else {
      if (!selectedWidgetDef) return;
      const def = JSON.parse(selectedWidgetDef);
      
      const finalTitle = customTitle.trim() !== '' ? customTitle.trim() : def.title;
      
      widgetData = { type: def.type, title: finalTitle, widgetConfig: { ...def.widgetConfig } };
      
      if (activeTab === 'system') {
        if (def.type === 'counter') {
          if (systemWidgetColor) widgetData.widgetConfig.color = systemWidgetColor;
          if (systemWidgetIcon) widgetData.widgetConfig.icon = systemWidgetIcon;
        } else if (def.type === 'list') {
          widgetData.widgetConfig.action = listAction;
        }
      }
      if (dsCongTy.length && hoTroNhieuCongTy(widgetData)) widgetData.widgetConfig.id_apps = dsCongTy;
    }
    
    onAdd(widgetData, activeTab === 'shortcut' ? null : selectedSize);
    
    // Reset form
    setSelectedWidgetDef('');
    setSystemWidgetColor('');
    setSystemWidgetIcon('');
    setCustomTitle('');
    setListAction('list');
    setDsCongTy([]);
    setShortcutData({ title: '', url: '', icon: 'link', color: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)' });
  }, [activeTab, shortcutData, selectedWidgetDef, systemWidgetColor, systemWidgetIcon, customTitle, listAction, dsCongTy, selectedSize, onAdd]);

  const isAddDisabled = (activeTab !== 'shortcut' && !selectedWidgetDef) || 
                        (activeTab === 'shortcut' && (!shortcutData.title || !shortcutData.url));

  const isLoadingSys = isLoadingListInfo || isLoadDataListConfig; 

  const parsedSelectedDef = useMemo(() => selectedWidgetDef ? JSON.parse(selectedWidgetDef) : null, [selectedWidgetDef]);

  // ============================================================================
  // CHUẨN HOÁ OPTIONS CHO AUTOCOMPLETE (cho phép gõ để tìm kiếm)
  // Mỗi option: { label, value (JSON def), group? }. "value" khớp với selectedWidgetDef.
  // ============================================================================
  const reportPivotOptions = useMemo(() => {
    const list = [];
    (availablePivots || []).forEach(p => {
      const def = { type: 'pivot', title: p.title, widgetConfig: { pivotConfigId: p._id || p.id } };
      list.push({ group: 'Phân tích (Pivot)', label: p.title || '(không tên)', value: JSON.stringify(def) });
    });
    (availableReports || []).forEach(r => {
      const def = { type: 'report', title: r.title, widgetConfig: { code: r.code } };
      list.push({ group: 'Báo cáo', label: r.title || r.code || '(không tên)', value: JSON.stringify(def) });
    });

    return list;
  }, [availableReports, availablePivots]);

  const workflowOptions = useMemo(() => (availableWorkflows || []).map(wf => ({
    label: wf.title || wf.name || wf.code || '(không tên)',
    value: JSON.stringify({ type: 'workflow', title: wf.title || wf.name, widgetConfig: { workflowId: wf._id || wf.id, code: wf.code } })
  })), [availableWorkflows]);

  const flowOptions = useMemo(() => (availableFlows || []).map(wf => ({
    label: wf.title || '(không tên)',
    value: JSON.stringify({ type: 'flow', title: wf.title, widgetConfig: { flowId: wf._id } })
  })), [availableFlows]);

  const customPageOptions = useMemo(() => (availableCustomPages || []).map(pg => ({
    label: pg.title || pg.code || '(không tên)',
    value: JSON.stringify({ type: 'custom', title: pg.title || pg.code, widgetConfig: { code: pg.code } })
  })), [availableCustomPages]);

  const systemOptions = useMemo(() => (combinedSystemWidgets || [])
    .map(sw => ({
      group: sw.type === 'counter' ? 'Thẻ đếm số liệu' : (sw.type === 'list' ? 'Danh sách dữ liệu' : 'Khác'),
      label: sw.title || '(không tên)',
      value: JSON.stringify({ type: sw.type, title: sw.title, widgetConfig: sw.widgetConfig })
    }))
    // Sắp xếp theo nhóm để MUI Autocomplete không lặp lại tiêu đề nhóm
    .sort((a, b) => a.group.localeCompare(b.group)), [combinedSystemWidgets]);

  const handlePickOption = useCallback((option) => {
    if (option && option.value) {
      setSelectedWidgetDef(option.value);
      try { setCustomTitle(JSON.parse(option.value).title || ''); }
      catch (e) { setCustomTitle(option.label || ''); }
    } else {
      setSelectedWidgetDef('');
      setCustomTitle('');
    }
    // Reset các lựa chọn phụ thuộc khi đổi nguồn
    setSystemWidgetColor('');
    setSystemWidgetIcon('');
    setListAction('list');
  }, []);

  // Bộ chọn nguồn có ô tìm kiếm dùng chung cho các tab
  const renderSourcePicker = ({ options, loading, label, placeholder, grouped, emptyText }) => (
    <ManagedDropdown
      type="autocomplete"
      options={options}
      loading={loading}
      autoHighlight
      getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.label || ''))}
      isOptionEqualToValue={(o, v) => o.value === v?.value}
      groupBy={grouped ? (o) => o.group : undefined}
      value={options.find(o => o.value === selectedWidgetDef) || null}
      onChange={(e, nv) => handlePickOption(nv)}
      noOptionsText={emptyText || 'Không tìm thấy'}
      loadingText="Đang tải dữ liệu..."
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle fontWeight="bold">Thêm Widget mới</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1, borderColor: 'divider', minHeight: 48,
            '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600, minWidth: 'auto', px: 2 }
          }}
        >
          <Tab value="pivot" icon={<AssessmentIcon fontSize="small" />} iconPosition="start" label="Báo cáo" />
          <Tab value="workflow" icon={<AccountTreeIcon fontSize="small" />} iconPosition="start" label="Quy trình" />
          <Tab value="flow" icon={<SettingsSuggestIcon fontSize="small" />} iconPosition="start" label="Tự động hoá" />
          <Tab value="system" icon={<WidgetsIcon fontSize="small" />} iconPosition="start" label="Tiện ích" />
          <Tab value="custom" icon={<DashboardCustomizeIcon fontSize="small" />} iconPosition="start" label="Trang tuỳ biến" />
          <Tab value="shortcut" icon={<BoltIcon fontSize="small" />} iconPosition="start" label="Phím tắt" />
        </Tabs>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* ================= FORM PIVOT ================= */}
          {activeTab === 'pivot' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight="500">
                  Chọn báo cáo hoặc báo cáo phân tích (Pivot) đã lưu
                </Typography>
                <Button size="small" startIcon={<HelpOutlineIcon />} onClick={() => setShowPivotGuide(!showPivotGuide)} sx={{ textTransform: 'none', borderRadius: 2 }}>
                  {showPivotGuide ? 'Ẩn hướng dẫn' : 'Hướng dẫn tạo nguồn'}
                </Button>
              </Box>

              <Collapse in={showPivotGuide}>
                <Alert severity="info" sx={{ borderRadius: 2, bgcolor: '#e3f2fd', color: '#014361', '& .MuiAlert-icon': { color: '#0288d1' } }}>
                  <AlertTitle sx={{ fontWeight: 'bold' }}>Cách tạo nguồn dữ liệu (Pivot) mới</AlertTitle>
                  <List dense disablePadding sx={{ '& .MuiListItemText-primary': { fontSize: '0.85rem' } }}>
                    <ListItem disablePadding sx={{ mb: 0.5 }}><ListItemText primary="1. Mở phân hệ báo cáo có chứa dữ liệu bạn cần." /></ListItem>
                    <ListItem disablePadding sx={{ mb: 0.5 }}><ListItemText primary="2. Chọn điều kiện lọc rồi tải dữ liệu." /></ListItem>
                    <ListItem disablePadding sx={{ mb: 0.5 }}><ListItemText primary="3. Chuyển sang chế độ xem phân tích đa chiều (Pivot)." /></ListItem>
                    <ListItem disablePadding sx={{ mb: 0.5 }}><ListItemText primary="4. Kéo thả các cột, dòng, chọn biểu đồ và Lưu cấu hình." /></ListItem>
                    <ListItem disablePadding><ListItemText primary="5. Quay lại đây, cấu hình vừa lưu sẽ xuất hiện!" /></ListItem>
                  </List>
                </Alert>
              </Collapse>

              {renderSourcePicker({
                options: reportPivotOptions,
                loading: isLoadingPivots || isLoadingReports,
                label: 'Tìm & chọn báo cáo',
                placeholder: 'Gõ tên báo cáo hoặc pivot để tìm nhanh...',
                grouped: true,
                emptyText: 'Chưa có báo cáo nào được lưu'
              })}
            </Box>
          )}

          {/* ================= FORM TRANG TUỲ BIẾN ================= */}
          {activeTab === 'custom' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                Nhúng thẳng một trang tuỳ biến vào dashboard — dùng khi thẻ đếm và danh sách có sẵn không diễn tả đủ
              </Typography>
              {renderSourcePicker({
                options: customPageOptions,
                loading: isLoadingCustomPages,
                label: 'Tìm & chọn trang tuỳ biến',
                placeholder: 'Gõ tên trang…',
                grouped: false,
                emptyText: 'Chưa có trang tuỳ biến nào. Cài một gói mở rộng hoặc tạo trang trong Quản lý module.'
              })}
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Trang nhúng chạy y như khi mở đầy đủ. Nên chọn ô rộng (8–12 cột) cho trang có bảng hoặc nhiều cột;
                trang thiết kế cho toàn màn hình sẽ chật trong ô nhỏ. Bấm tiêu đề widget để mở trang ở dạng đầy đủ.
              </Alert>
            </Box>
          )}

          {/* ================= FORM WORKFLOW ================= */}
          {activeTab === 'workflow' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                Hiển thị bảng theo dõi của một quy trình cụ thể
              </Typography>
              {renderSourcePicker({
                options: workflowOptions,
                loading: isLoadingWorkflows,
                label: 'Tìm & chọn quy trình',
                placeholder: 'Gõ tên quy trình...',
                grouped: false,
                emptyText: 'Chưa có quy trình nào trên hệ thống'
              })}
            </Box>
          )}

          {/* ================= FORM FLOW ================= */}
          {activeTab === 'flow' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                Hiển thị quy trình xử lý tự động
              </Typography>
              {renderSourcePicker({
                options: flowOptions,
                loading: isLoadingFlows,
                label: 'Tìm & chọn luồng tự động',
                placeholder: 'Gõ tên luồng...',
                grouped: false,
                emptyText: 'Chưa có luồng nào trên hệ thống'
              })}
            </Box>
          )}

          {/* ================= FORM SYSTEM ================= */}
          {activeTab === 'system' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {renderSourcePicker({
                options: systemOptions,
                loading: isLoadingSys,
                label: 'Tìm & chọn tiện ích',
                placeholder: 'Gõ tên tiện ích...',
                grouped: true,
                emptyText: 'Không có tiện ích nào'
              })}

              {parsedSelectedDef && !isLoadingSys && parsedSelectedDef.type === 'counter' && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Chọn Icon</InputLabel>
                    <ManagedDropdown type="select" value={systemWidgetIcon} label="Chọn Icon" onChange={e => setSystemWidgetIcon(e.target.value)}>
                      <MenuItem value=""><span style={{ color: '#aaa', fontStyle: 'italic' }}>Không dùng Icon</span></MenuItem>
                      {iconOptionsNodes}
                    </ManagedDropdown>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Màu sắc</InputLabel>
                    <ManagedDropdown type="select" value={systemWidgetColor} label="Màu sắc" onChange={e => setSystemWidgetColor(e.target.value)}>
                      <MenuItem value=""><span style={{ color: '#aaa', fontStyle: 'italic' }}>Mặc định</span></MenuItem>
                      {colorOptionsNodes}
                    </ManagedDropdown>
                  </FormControl>
                </Box>
              )}

              {parsedSelectedDef && !isLoadingSys && parsedSelectedDef.type === 'list' && (
                 <FormControl component="fieldset">
                 <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>Hành động của Widget</FormLabel>
                 <RadioGroup row value={listAction} onChange={(e) => setListAction(e.target.value)}>
                   <FormControlLabel value="list" control={<Radio size="small" />} label="Hiển thị danh sách" />
                   <FormControlLabel value="add" control={<Radio size="small" />} label="Thêm nhanh (Form nhập liệu)" />
                 </RadioGroup>
               </FormControl>
              )}
            </Box>
          )}

          {/* ================= TÙY CHỈNH CHUNG ================= */}
          {['pivot', 'system', 'workflow','flow'].includes(activeTab) && selectedWidgetDef && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField 
                label="Tiêu đề hiển thị" 
                fullWidth 
                value={customTitle} 
                onChange={e => setCustomTitle(e.target.value)} 
                helperText="Bạn có thể đổi tên hiển thị của Widget này trên Dashboard"
              />

              <FormControl fullWidth>
                <InputLabel>Kích thước chiều ngang</InputLabel>
                <ManagedDropdown type="select" value={selectedSize} label="Kích thước chiều ngang" onChange={(e) => setSelectedSize(e.target.value)}>
                  <MenuItem value={12}>Toàn màn hình (12 cột)</MenuItem>
                  <MenuItem value={6}>Trung bình (6 cột)</MenuItem>
                  <MenuItem value={4}>Nhỏ (4 cột)</MenuItem>
                  <MenuItem value={3}>Rất nhỏ (3 cột)</MenuItem>
                </ManagedDropdown>
              </FormControl>
            </Box>
          )}

          {/* ================= CÔNG TY LẤY DỮ LIỆU (widgetConfig.id_apps) ================= */}
          {activeTab !== 'shortcut' && parsedSelectedDef && hoTroNhieuCongTy({
            type: parsedSelectedDef.type,
            widgetConfig: { ...(parsedSelectedDef.widgetConfig || {}), ...(activeTab === 'system' && parsedSelectedDef.type === 'list' ? { action: listAction } : {}) },
          }) && (
            <ChonCongTyWidget type={parsedSelectedDef.type} value={dsCongTy} onChange={setDsCongTy} enabled={open} />
          )}

          {/* 👇 ================= FORM SHORTCUT BẰNG AUTOCOMPLETE ================= */}
          {activeTab === 'shortcut' && (
            <>
              <ManagedDropdown type="autocomplete"
                options={flattenedModules}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => option.title}
                value={flattenedModules.find(m => m.path === shortcutData.url) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setShortcutData(prev => ({
                      ...prev,
                      title: newValue.title,
                      url: newValue.path
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Tìm chức năng có sẵn (Tùy chọn)" 
                    placeholder="Gõ tên chức năng để tìm nhanh..."
                    helperText="Chọn từ danh sách để tự động điền Tên và Đường dẫn, hoặc tự nhập tay ở các ô bên dưới."
                  />
                )}
                noOptionsText="Không tìm thấy chức năng"
                fullWidth
              />

              <TextField 
                label="Tên phím tắt" 
                placeholder="VD: Tạo ngoại tệ mới" 
                fullWidth 
                value={shortcutData.title} 
                onChange={e => setShortcutData(prev => ({...prev, title: e.target.value}))} 
              />
              <TextField 
                label="Đường dẫn (URL)" 
                placeholder="VD: /page/currency hoặc https://google.com" 
                fullWidth 
                value={shortcutData.url} 
                onChange={e => setShortcutData(prev => ({...prev, url: e.target.value}))} 
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Chọn Icon</InputLabel>
                  <ManagedDropdown type="select" value={shortcutData.icon} label="Chọn Icon" onChange={e => setShortcutData(prev => ({...prev, icon: e.target.value}))}>
                    {iconOptionsNodes}
                  </ManagedDropdown>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel>Màu sắc</InputLabel>
                  <ManagedDropdown type="select" value={shortcutData.color} label="Màu sắc" onChange={e => setShortcutData(prev => ({...prev, color: e.target.value}))}>
                    {colorOptionsNodes}
                  </ManagedDropdown>
                </FormControl>
              </Box>
              <Typography variant="caption" color="text.secondary">* Kích thước của phím tắt được mặc định là khối vuông nhỏ.</Typography>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">Hủy</Button>
        <Button onClick={handleConfirmAdd} variant="contained" disabled={isAddDisabled || isLoadingSys}>
          Thêm vào Dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default React.memo(WidgetAddDialog);